import { z } from 'zod';

const portSchema = z.coerce.number().int().min(1).max(65_535);
const placeholderPattern = /replace-with/i;
const minimumSecretLength = 32;

const secretSchema = z.string().min(minimumSecretLength);

/** Compose passes an unset optional variable as an empty string; treat that as not set. */
const optionalTextSchema = z.preprocess(
  (value) => (value === '' ? undefined : value),
  z.string().min(1).optional(),
);

const nodeEnvironmentSchema = z.enum(['development', 'test', 'production']).default('development');

// An origin has no path, query or fragment, so it compares directly with the browser's Origin header.
const originSchema = z.url({ protocol: /^https?$/ }).regex(/^https?:\/\/[^/?#\s]+$/, {
  message: 'Must be an origin without a path or trailing slash',
});

const databaseUrlSchema = z.url({ protocol: /^postgres(ql)?$/ });

/** Variables that must be present and non-placeholder before a production process starts. */
const productionRequiredNames = [
  'MIGRATION_DATABASE_URL',
  'PUBLIC_ORIGIN',
  'OIDC_ISSUER',
  'OIDC_CLIENT_ID',
  'OBJECT_STORAGE_ENDPOINT',
  'OBJECT_STORAGE_BUCKET',
  'OBJECT_STORAGE_ACCESS_KEY',
  'OBJECT_STORAGE_SECRET_KEY',
  'SMTP_HOST',
  'SMTP_PORT',
  'SESSION_SECRET',
  'PERSONAL_NUMBER_HMAC_KEY',
] as const;

export const apiEnvironmentSchema = z
  .object({
    NODE_ENV: nodeEnvironmentSchema,
    API_PORT: portSchema.default(3000),
    DATABASE_URL: databaseUrlSchema,
    MIGRATION_DATABASE_URL: databaseUrlSchema.optional(),
    /** Origin users reach the application on; the API sits behind the same-origin reverse proxy. */
    PUBLIC_ORIGIN: originSchema.optional(),
    OIDC_ISSUER: z.url().optional(),
    OIDC_CLIENT_ID: z.string().min(1).optional(),
    OBJECT_STORAGE_ENDPOINT: z.url().optional(),
    OBJECT_STORAGE_BUCKET: z.string().min(1).optional(),
    OBJECT_STORAGE_ACCESS_KEY: z.string().min(1).optional(),
    OBJECT_STORAGE_SECRET_KEY: z.string().min(1).optional(),
    SMTP_HOST: z.string().min(1).optional(),
    SMTP_PORT: portSchema.optional(),
    /** Implicit TLS (usually port 465). Off means STARTTLS is used when the server offers it. */
    SMTP_SECURE: z.stringbool().default(false),
    /** Credentials for an authenticating mail server; both or neither. */
    SMTP_USER: optionalTextSchema,
    SMTP_PASSWORD: optionalTextSchema,
    SESSION_SECRET: secretSchema.optional(),
    PERSONAL_NUMBER_HMAC_KEY: secretSchema.optional(),
    /** Staff roles need an MFA sign-in. Defaults to on in production and off for local development. */
    STAFF_MFA_REQUIRED: z.stringbool().optional(),
  })
  .superRefine((environment, context) => {
    if ((environment.SMTP_USER === undefined) !== (environment.SMTP_PASSWORD === undefined)) {
      context.addIssue({
        code: 'custom',
        path: ['SMTP_PASSWORD'],
        message: 'SMTP_USER and SMTP_PASSWORD are set together',
      });
    }
    if (environment.NODE_ENV !== 'production') return;
    for (const name of productionRequiredNames) {
      const value = environment[name];
      if (value === undefined) {
        context.addIssue({ code: 'custom', path: [name], message: 'Required in production' });
      } else if (typeof value === 'string' && placeholderPattern.test(value)) {
        context.addIssue({
          code: 'custom',
          path: [name],
          message: 'Placeholder value in production',
        });
      }
    }
  });

export type ApiEnvironment = z.infer<typeof apiEnvironmentSchema>;

export const workerEnvironmentSchema = z
  .object({
    NODE_ENV: nodeEnvironmentSchema,
    WORKER_HEALTH_PORT: portSchema.default(3001),
    /** Connection of the non-owner `stm_worker` role. Without it the worker only answers health checks. */
    WORKER_DATABASE_URL: databaseUrlSchema.optional(),
    /** How often the worker looks for clubs whose automatic fee run is due. */
    FEE_RUN_INTERVAL_MINUTES: z.coerce.number().int().min(1).max(1440).default(15),
  })
  .superRefine((environment, context) => {
    if (environment.NODE_ENV !== 'production') return;
    if (environment.WORKER_DATABASE_URL === undefined) {
      context.addIssue({
        code: 'custom',
        path: ['WORKER_DATABASE_URL'],
        message: 'Required in production',
      });
    } else if (placeholderPattern.test(environment.WORKER_DATABASE_URL)) {
      context.addIssue({
        code: 'custom',
        path: ['WORKER_DATABASE_URL'],
        message: 'Placeholder value in production',
      });
    }
  });

export type WorkerEnvironment = z.infer<typeof workerEnvironmentSchema>;
